import React, { useMemo } from "react";
import { Badge, Box, Stack, Text, Timeline } from "@mantine/core";
import {
  IconClipboardPlus,
  IconClockX,
  IconEdit,
  IconEye,
  IconFileCheck,
} from "@tabler/icons-react";
import {
  FormTemplateField,
  HistoryChangeType,
  HistoryEntry,
} from "../../../types/formTemplates";

interface FormHistoryTimelineProps {
  history: HistoryEntry[];
  fields?: FormTemplateField[];
}

interface GroupedEntry {
  header: HistoryEntry; // form_created | form_opened | draft_saved | form_submitted | form_expired
  children: HistoryEntry[]; // field_updated entries attributed to this save
}

function groupHistory(entries: HistoryEntry[]): GroupedEntry[] {
  // Backend returns newest-first ordered by created_at DESC. Per-save
  // field_updated rows share a batch created_at with their draft_saved.
  // We walk top-down and, for each non-field_updated "header" entry,
  // attach the adjacent field_updated rows that follow it (still
  // newest-first within the group — we keep the order the backend gave).
  const groups: GroupedEntry[] = [];
  let i = 0;
  while (i < entries.length) {
    const entry = entries[i];
    if (entry.change_type === "field_updated") {
      // Orphan field_updated — shouldn't happen per contract, but keep it
      // visible by wrapping in its own minimal group.
      groups.push({ header: entry, children: [] });
      i += 1;
      continue;
    }
    const children: HistoryEntry[] = [];
    let j = i + 1;
    while (j < entries.length && entries[j].change_type === "field_updated") {
      children.push(entries[j]);
      j += 1;
    }
    groups.push({ header: entry, children });
    i = j;
  }
  return groups;
}

const HEADER_ICON: Record<HistoryChangeType, React.ComponentType<any>> = {
  form_created: IconClipboardPlus,
  form_opened: IconEye,
  draft_saved: IconEdit,
  form_submitted: IconFileCheck,
  form_expired: IconClockX,
  field_updated: IconEdit,
};

const HEADER_LABEL: Record<HistoryChangeType, string> = {
  form_created: "Created",
  form_opened: "Opened",
  draft_saved: "Draft saved",
  form_submitted: "Submitted & signed",
  form_expired: "Expired",
  field_updated: "Field updated",
};

const HEADER_COLOR: Record<HistoryChangeType, string> = {
  form_created: "gray",
  form_opened: "blue",
  draft_saved: "orange",
  form_submitted: "teal",
  form_expired: "red",
  field_updated: "gray",
};

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function actorName(entry: HistoryEntry): string {
  if (entry.actor_name) return entry.actor_name;
  if (entry.actor_id)
    return `${entry.actor_type} #${entry.actor_id.slice(0, 6)}`;
  return entry.actor_type;
}

/** Compact, human-readable preview of a field value for the timeline. */
function previewValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") return "—";
    return trimmed.length > 60 ? `${trimmed.slice(0, 57)}…` : trimmed;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return "—";
    return value.length === 1
      ? previewValue(value[0])
      : `${value.length} items`;
  }
  if (typeof value === "object") {
    return "(updated)";
  }
  return String(value);
}

export const FormHistoryTimeline: React.FC<FormHistoryTimelineProps> = ({
  history,
  fields,
}) => {
  const groups = useMemo(() => groupHistory(history), [history]);

  const labelByFieldId = useMemo(() => {
    const map = new Map<string, string>();
    for (const f of fields ?? []) {
      if (f.id && f.label) map.set(f.id, f.label);
    }
    return map;
  }, [fields]);

  const fieldDisplay = (id: string | null): string => {
    if (!id) return "field";
    return labelByFieldId.get(id) ?? id;
  };

  if (groups.length === 0) {
    return (
      <Text c="dimmed" size="sm" ta="center" p="md">
        No history yet.
      </Text>
    );
  }

  return (
    <Timeline active={-1} bulletSize={18} lineWidth={2}>
      {groups.map((g) => {
        const Icon = HEADER_ICON[g.header.change_type];
        const headerCount =
          g.header.change_type === "draft_saved"
            ? typeof (g.header.metadata as Record<string, unknown> | null)
                ?.change_count === "number"
              ? ((g.header.metadata as Record<string, unknown>)
                  .change_count as number)
              : g.children.length
            : null;
        return (
          <Timeline.Item
            key={g.header.id}
            title={
              <Box>
                <Text fw={600} size="sm">
                  {HEADER_LABEL[g.header.change_type]}
                  {headerCount !== null && headerCount > 0 && (
                    <Text component="span" c="dimmed" fw={400} size="xs" ml={6}>
                      ({headerCount} field
                      {headerCount === 1 ? "" : "s"} changed)
                    </Text>
                  )}
                </Text>
              </Box>
            }
            bullet={<Icon size={12} />}
            color={HEADER_COLOR[g.header.change_type]}
          >
            <Text size="xs" c="dimmed">
              {formatDateTime(g.header.created_at)} • {actorName(g.header)}
            </Text>
            {g.header.change_type === "draft_saved" &&
              g.children.length > 0 && (
                <Stack gap={4} mt={6}>
                  {g.children.map((c) => (
                    <Box key={c.id}>
                      <Badge
                        variant="light"
                        color="gray"
                        size="xs"
                        mr={6}
                        style={{ textTransform: "none" }}
                      >
                        {fieldDisplay(c.field_id)}
                      </Badge>
                      <Text component="span" size="xs" c="dimmed">
                        {previewValue(c.old_value)} →{" "}
                        <Text component="span" c="dark" fw={500}>
                          {previewValue(c.new_value)}
                        </Text>
                      </Text>
                    </Box>
                  ))}
                </Stack>
              )}
          </Timeline.Item>
        );
      })}
    </Timeline>
  );
};

// Exported for direct unit testing.
export { groupHistory };
